import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { localizeDict } from "@/lib/localText";
import { countryList } from "@/lib/countries";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileUpload, resolveMediaUrl } from "@/components/FileUpload";
import { imageVariant, imageVariantFallback } from "@/lib/mediaUrl";
import { COVER_THEMES, coverThemeFor } from "@/lib/churchCover";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { AnnexPicker } from "@/components/church/AnnexPicker";
import { Church, ImagePlus, AlertTriangle, Search, BadgeCheck, Loader2, Info, Clock } from "lucide-react";

const L10N = {
  fr: { name: "Nom de l'église", pastor_title: "Titre du responsable", pastor_name: "Nom du responsable", denomination: "Obédience / Dénomination", country: "Pays", city: "Ville", address: "Adresse", description: "Description", website: "Site web", choose: "Choisir...", logo: "Logo / photo de profil", cover: "Bannière de couverture", cover_theme: "Ou choisis un thème de couverture", owner: "Responsable (email du compte)", owner_hint: "Ce compte pourra éditer la fiche et diffuser des directs au nom de l'église.", remove: "Retirer", change: "Changer", resp_hint: "Le responsable doit être un prédicateur possédant un compte Yalola. Un prédicateur non encore vérifié (KYC) peut être proposé : un admin validera la page.", resp_search_ph: "Rechercher un prédicateur…", resp_none: "Aucun prédicateur trouvé. Il doit d'abord créer un compte Yalola de type prédicateur.", resp_verified: "Prédicateur vérifié", resp_pending: "Vérification en attente", handle: "Lien personnalisé (identifiant)", handle_hint: "Sert d'URL courte et de lien de partage : yalola.app/churches/identifiant", annex_toggle: "Cette église est une annexe de mon église", annex_choose: "Église mère", annex_hint: "Le lien « Annexe de … » s'affichera sur les deux pages après validation par un admin." },
  en: { name: "Church name", pastor_title: "Leader's title", pastor_name: "Leader's name", denomination: "Denomination", country: "Country", city: "City", address: "Address", description: "Description", website: "Website", choose: "Choose...", logo: "Logo / profile photo", cover: "Cover banner", cover_theme: "Or pick a cover theme", owner: "Responsible (account email)", owner_hint: "This account will be able to edit the page and broadcast lives on behalf of the church.", remove: "Remove", change: "Change", resp_hint: "The responsible must be a preacher with a Yalola account. A not-yet-verified (KYC) preacher can be proposed: an admin will validate the page.", resp_search_ph: "Search a preacher…", resp_none: "No preacher found. They must first create a preacher Yalola account.", resp_verified: "Verified preacher", resp_pending: "Verification pending", handle: "Custom link (handle)", handle_hint: "Used as the short URL and share link: yalola.app/churches/handle", annex_toggle: "This church is a branch of my church", annex_choose: "Mother church", annex_hint: "The “Branch of …” link will appear on both pages once an admin validates it." },
  es: { name: "Nombre de la iglesia", pastor_title: "Título del responsable", pastor_name: "Nombre del responsable", denomination: "Denominación", country: "País", city: "Ciudad", address: "Dirección", description: "Descripción", website: "Sitio web", choose: "Elegir...", logo: "Logo / foto de perfil", cover: "Banner de portada", cover_theme: "O elige un tema de portada", owner: "Responsable (email de la cuenta)", owner_hint: "Esta cuenta podrá editar la página y transmitir directos en nombre de la iglesia.", remove: "Quitar", change: "Cambiar", resp_hint: "El responsable debe ser un predicador con una cuenta Yalola. Un predicador aún no verificado (KYC) puede ser propuesto: un admin validará la página.", resp_search_ph: "Buscar un predicador…", resp_none: "No se encontró ningún predicador. Primero debe crear una cuenta Yalola de tipo predicador.", resp_verified: "Predicador verificado", resp_pending: "Verificación pendiente", handle: "Enlace personalizado (identificador)", handle_hint: "Se usa como URL corta y enlace para compartir: yalola.app/churches/identificador", annex_toggle: "Esta iglesia es un anexo de mi iglesia", annex_choose: "Iglesia madre", annex_hint: "El enlace « Anexo de … » aparecerá en ambas páginas tras la validación de un admin." },
  pt: { name: "Nome da igreja", pastor_title: "Título do responsável", pastor_name: "Nome do responsável", denomination: "Denominação", country: "País", city: "Cidade", address: "Endereço", description: "Descrição", website: "Site", choose: "Escolher...", logo: "Logo / foto de perfil", cover: "Banner de capa", cover_theme: "Ou escolhe um tema de capa", owner: "Responsável (email da conta)", owner_hint: "Esta conta poderá editar a página e transmitir diretos em nome da igreja.", remove: "Remover", change: "Alterar", resp_hint: "O responsável deve ser um pregador com uma conta Yalola. Um pregador ainda não verificado (KYC) pode ser proposto: um admin validará a página.", resp_search_ph: "Pesquisar um pregador…", resp_none: "Nenhum pregador encontrado. Ele deve primeiro criar uma conta Yalola do tipo pregador.", resp_verified: "Pregador verificado", resp_pending: "Verificação pendente", handle: "Link personalizado (identificador)", handle_hint: "Usado como URL curta e link de partilha: yalola.app/churches/identificador", annex_toggle: "Esta igreja é um anexo da minha igreja", annex_choose: "Igreja-mãe", annex_hint: "A ligação « Anexo de … » aparecerá nas duas páginas após validação por um admin." },
};

const FALLBACK_DENOMS = [
  "Évangélique", "Pentecôtiste", "Baptiste", "Catholique", "Orthodoxe",
  "Méthodiste", "Presbytérienne", "Réveil", "Non-dénominationnelle", "Autre",
];

export const emptyChurch = (init = {}) => ({
  name: "", pastor_title: "", pastor_name: "", pastor_user_id: "", denomination: "",
  country: "", city: "", address: "", description: "", website: "",
  logo_url: "", cover_url: "", cover_theme: "", owner_email: "", ...init,
});

export const churchFormValid = (v, requiredKeys = ["name", "city", "country"]) =>
  requiredKeys.every((k) => (v?.[k] || "").trim());

// Full requirement set for the public "suggest a church" form (everything but website).
export const SUGGEST_REQUIRED_KEYS = ["name", "pastor_title", "pastor_name", "denomination", "country", "city", "address", "description"];

// Verified-preacher autocomplete for the church "responsible" field.
const PreacherPicker = ({ value, onChange, testPrefix, tr }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const reqRef = useRef(0);
  const selectedId = value.pastor_user_id;

  useEffect(() => {
    if (selectedId) return undefined;
    setLoading(true);
    const rid = ++reqRef.current;
    const timer = setTimeout(() => {
      api.get("/users/search/preachers", { params: { q: q.trim() } })
        .then(({ data }) => { if (rid === reqRef.current) setResults(data?.users || []); })
        .catch(() => { if (rid === reqRef.current) setResults([]); })
        .finally(() => { if (rid === reqRef.current) setLoading(false); });
    }, 300);
    return () => clearTimeout(timer);
  }, [q, selectedId]);

  const choose = (u) => { onChange({ ...value, pastor_user_id: u.id, pastor_name: u.name, pastor_verified: !!u.is_verified }); setOpen(false); setQ(""); };
  const clear = () => onChange({ ...value, pastor_user_id: "", pastor_name: "", pastor_verified: false });

  if (selectedId) {
    const verified = !!value.pastor_verified;
    return (
      <div className="flex items-center gap-2 rounded-xl border px-3 py-2" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }} data-testid={`${testPrefix}-resp-selected`}>
        {verified
          ? <BadgeCheck size={18} style={{ color: "var(--yalola-primary)" }} className="shrink-0" />
          : <Clock size={18} style={{ color: "#B8860B" }} className="shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">{value.pastor_name}</div>
          <div className="text-[11px]" style={{ color: verified ? "var(--yalola-text-muted)" : "#B8860B" }} data-testid={`${testPrefix}-resp-status`}>{verified ? tr("resp_verified") : tr("resp_pending")}</div>
        </div>
        <button type="button" onClick={clear} className="text-xs chip shrink-0" data-testid={`${testPrefix}-resp-change`}>{tr("change")}</button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--yalola-text-muted)" }} />
      <Input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={tr("resp_search_ph")}
        className="pl-8"
        data-testid={`${testPrefix}-resp-search`}
      />
      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-xl border shadow-lg max-h-56 overflow-y-auto" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }} data-testid={`${testPrefix}-resp-results`}>
          {loading ? (
            <div className="px-3 py-3 text-xs flex items-center gap-1.5" style={{ color: "var(--yalola-text-muted)" }}><Loader2 size={13} className="animate-spin" /> …</div>
          ) : results.length ? results.map((u) => (
            <button key={u.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => choose(u)} className="w-full flex items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-[color:var(--yalola-surface-alt)]" data-testid={`${testPrefix}-resp-result-${u.id}`}>
              <span className="h-7 w-7 rounded-full overflow-hidden grid place-items-center shrink-0" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)", fontWeight: 700, fontSize: 11 }}>
                {/* Lot 680 — liste de membres : variante 128 px (comme partout) au lieu de la photo originale. */}
                {u.avatar_url ? <img src={imageVariant(u.avatar_url, 48)} alt="" className="h-full w-full object-cover" onError={(e) => imageVariantFallback(e.currentTarget)} /> : (u.name?.[0]?.toUpperCase() || "?")}
              </span>
              <span className="flex-1 min-w-0">
                <span className="text-sm font-medium truncate flex items-center gap-1">{u.name}{u.is_verified && <BadgeCheck size={13} style={{ color: "var(--yalola-primary)" }} />}</span>
                {u.is_verified
                  ? (u.city && <span className="block text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{u.city}</span>)
                  : <span className="block text-[11px] flex items-center gap-1" style={{ color: "#B8860B" }}><Clock size={10} /> {tr("resp_pending")}</span>}
              </span>
            </button>
          )) : (
            <div className="px-3 py-3 text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid={`${testPrefix}-resp-none`}>{tr("resp_none")}</div>
          )}
        </div>
      )}
    </div>
  );
};

// Shared church form — SAME fields everywhere (registration, suggest, admin add/edit).
// `showMedia` adds logo/cover uploads + responsable (owner) — used by admins.
export const ChurchForm = ({ value, onChange, requiredKeys = ["name", "city", "country"], testPrefix = "church-form", showMedia = false, checkDuplicate = false, preacherPicker = false }) => {
  const { config, lang, t } = useApp();
  const [dup, setDup] = useState(null);
  const [dupChecking, setDupChecking] = useState(false);
  const dupReqRef = useRef(0);
  // Live duplicate detection while the user types the church name (suggest forms).
  useEffect(() => {
    if (!checkDuplicate) return undefined;
    const name = (value?.name || "").trim();
    if (name.length < 3) { setDup(null); setDupChecking(false); return undefined; }
    setDupChecking(true);
    const rid = ++dupReqRef.current;
    const timer = setTimeout(() => {
      api.get("/churches/check-duplicate", { params: { name, city: value?.city || "", country: value?.country || "", pastor_name: value?.pastor_name || "" } })
        .then(({ data }) => { if (rid === dupReqRef.current) setDup(data?.duplicate ? data : null); })
        .catch(() => { if (rid === dupReqRef.current) setDup(null); })
        .finally(() => { if (rid === dupReqRef.current) setDupChecking(false); });
    }, 500);
    return () => clearTimeout(timer);
  }, [checkDuplicate, value?.name, value?.city, value?.country, value?.pastor_name]);

  const _L = localizeDict(L10N, lang, config?.translations, "churchform");
  const tr = (k) => _L[k];
  const denoms = (config?.denominations?.length ? config.denominations.map((d) => d.name) : FALLBACK_DENOMS);
  const titles = config?.settings?.leader_titles?.length ? config.settings.leader_titles : ["Pasteur"];
  const countries = countryList(lang);
  const set = (k, v) => onChange({ ...value, [k]: v });
  const star = (k) => (requiredKeys.includes(k) ? " *" : "");

  return (
    <div className="space-y-3" data-testid={testPrefix}>
      {showMedia && (
        <div className="space-y-3">
          <div>
            <Label>{tr("cover")}</Label>
            <div className="mt-1 rounded-xl overflow-hidden border relative" style={{ borderColor: "var(--yalola-border)" }}>
              <div className="h-24 w-full" style={{ background: coverThemeFor(value).gradient }}>
                {value.cover_url && <img src={resolveMediaUrl(value.cover_url)} alt="" className="h-full w-full object-cover" />}
              </div>
              <div className="absolute bottom-2 right-2 flex gap-2">
                {value.cover_url && <button type="button" onClick={() => set("cover_url", "")} className="text-xs bg-black/60 text-white rounded-full px-2 py-1" data-testid={`${testPrefix}-cover-remove`}>{tr("remove")}</button>}
                <FileUpload accept="image/*" onUploaded={(url) => set("cover_url", url)} variant="compact" buttonLabel={tr("change")} testId={`${testPrefix}-cover`} />
              </div>
            </div>
            {!value.cover_url && (
              <div className="mt-2">
                <div className="text-[11px] mb-1.5" style={{ color: "var(--yalola-text-muted)" }}>{tr("cover_theme")}</div>
                <div className="flex flex-wrap gap-1.5" data-testid={`${testPrefix}-cover-themes`}>
                  <button
                    type="button"
                    onClick={() => set("cover_theme", "")}
                    className="h-7 w-7 rounded-full border grid place-items-center text-[10px] font-bold"
                    style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)", borderColor: !value.cover_theme ? "var(--yalola-primary)" : "var(--yalola-border)", boxShadow: !value.cover_theme ? "0 0 0 2px var(--yalola-primary)" : "none" }}
                    title="Auto"
                    data-testid={`${testPrefix}-cover-theme-auto`}
                  >A</button>
                  {COVER_THEMES.map((th) => (
                    <button
                      key={th.key}
                      type="button"
                      onClick={() => set("cover_theme", th.key)}
                      className="h-7 w-7 rounded-full border"
                      style={{ background: th.gradient, borderColor: value.cover_theme === th.key ? "var(--yalola-primary)" : "var(--yalola-border)", boxShadow: value.cover_theme === th.key ? "0 0 0 2px var(--yalola-primary)" : "none" }}
                      title={th.label}
                      data-testid={`${testPrefix}-cover-theme-${th.key}`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="h-16 w-16 rounded-2xl overflow-hidden border grid place-items-center shrink-0" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-secondary)" }}>
              {value.logo_url ? <img src={resolveMediaUrl(value.logo_url)} alt="" className="h-full w-full object-cover" /> : <Church size={26} className="text-white" />}
            </div>
            <div>
              <Label>{tr("logo")}</Label>
              <div className="flex gap-2 mt-1">
                <FileUpload accept="image/*" onUploaded={(url) => set("logo_url", url)} variant="compact" buttonLabel={<><ImagePlus size={14} className="mr-1" /> {tr("change")}</>} testId={`${testPrefix}-logo`} />
                {value.logo_url && <button type="button" onClick={() => set("logo_url", "")} className="text-xs chip" data-testid={`${testPrefix}-logo-remove`}>{tr("remove")}</button>}
              </div>
            </div>
          </div>
        </div>
      )}
      <div><Label>{tr("name")}{star("name")}</Label><Input value={value.name || ""} onChange={(e) => set("name", e.target.value)} data-testid={`${testPrefix}-name`} /></div>
      {checkDuplicate && (dup ? (
        <div className="rounded-xl border p-3 flex items-start gap-2.5" style={{ borderColor: "#E0A63C", background: "#FDF6E7" }} data-testid={`${testPrefix}-dup-warning`}>
          <AlertTriangle size={18} style={{ color: "#B8860B" }} className="shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold" style={{ color: "#8A6D1F" }}>{t("church.dup_warning_title", "Cette église existe peut-être déjà")}</div>
            <div className="text-xs mt-0.5" style={{ color: "#8A6D1F" }}>{t("church.dup_warning_body", "Une église similaire est déjà sur Yalola. Suis plutôt sa page.")}</div>
            {dup.match && (
              <Link to={`/churches/${dup.match.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-1.5 text-xs font-semibold hover:underline" style={{ color: "var(--yalola-primary)" }} data-testid={`${testPrefix}-dup-link`}>
                {dup.match.name}{dup.match.city ? ` — ${dup.match.city}` : ""} · {t("church.dup_view", "Voir la page")}
              </Link>
            )}
          </div>
        </div>
      ) : dupChecking ? (
        <div className="text-[11px] flex items-center gap-1.5" style={{ color: "var(--yalola-text-muted)" }} data-testid={`${testPrefix}-dup-checking`}>{t("church.dup_checking", "Vérification…")}</div>
      ) : null)}
      {preacherPicker ? (
        <div className="space-y-3">
          <div>
            <Label>{tr("pastor_title")}{star("pastor_title")}</Label>
            <Select value={value.pastor_title || undefined} onValueChange={(v) => set("pastor_title", v)}>
              <SelectTrigger data-testid={`${testPrefix}-pastor-title`}><SelectValue placeholder={tr("choose")} /></SelectTrigger>
              <SelectContent className="max-h-64">{titles.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>{tr("pastor_name")}{star("pastor_name")}</Label>
            <PreacherPicker value={value} onChange={onChange} testPrefix={testPrefix} tr={tr} />
            <p className="text-[11px] mt-1.5 flex items-start gap-1.5" style={{ color: "var(--yalola-text-muted)" }} data-testid={`${testPrefix}-resp-hint`}>
              <Info size={13} className="shrink-0 mt-0.5" /> {tr("resp_hint")}
            </p>
          </div>
          <AnnexPicker value={value} onChange={onChange} testPrefix={testPrefix} tr={tr} />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>{tr("pastor_title")}{star("pastor_title")}</Label>
            <Select value={value.pastor_title || undefined} onValueChange={(v) => set("pastor_title", v)}>
              <SelectTrigger data-testid={`${testPrefix}-pastor-title`}><SelectValue placeholder={tr("choose")} /></SelectTrigger>
              <SelectContent className="max-h-64">{titles.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>{tr("pastor_name")}{star("pastor_name")}</Label><Input value={value.pastor_name || ""} onChange={(e) => set("pastor_name", e.target.value)} data-testid={`${testPrefix}-pastor-name`} /></div>
        </div>
      )}
      <div>
        <Label>{tr("denomination")}{star("denomination")}</Label>
        <Select value={value.denomination || undefined} onValueChange={(v) => set("denomination", v)}>
          <SelectTrigger data-testid={`${testPrefix}-denomination`}><SelectValue placeholder={tr("choose")} /></SelectTrigger>
          <SelectContent className="max-h-64">{denoms.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>{tr("country")}{star("country")}</Label>
          <Select value={value.country || undefined} onValueChange={(v) => set("country", v)}>
            <SelectTrigger data-testid={`${testPrefix}-country`}><SelectValue placeholder={tr("choose")} /></SelectTrigger>
            <SelectContent className="max-h-64">{countries.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>{tr("city")}{star("city")}</Label><CityAutocomplete value={value.city || ""} country={value.country || ""} onChange={(v) => set("city", v)} testId={`${testPrefix}-city`} /></div>
      </div>
      <div><Label>{tr("address")}{star("address")}</Label><Input value={value.address || ""} onChange={(e) => set("address", e.target.value)} data-testid={`${testPrefix}-address`} /></div>
      <div><Label>{tr("description")}{star("description")}</Label><Textarea rows={2} value={value.description || ""} onChange={(e) => set("description", e.target.value)} data-testid={`${testPrefix}-description`} /></div>
      <div><Label>{tr("website")}</Label><Input value={value.website || ""} onChange={(e) => set("website", e.target.value)} data-testid={`${testPrefix}-website`} /></div>
      {showMedia && value.id && (
        <div>
          <Label>{tr("handle")}</Label>
          <div className="flex items-center gap-1.5">
            <span className="text-base font-bold" style={{ color: "var(--yalola-text-muted)" }}>@</span>
            <Input value={value.handle || ""} onChange={(e) => set("handle", e.target.value.replace(/^@+/, "").toLowerCase())} placeholder={t("church.handle_ph", "mon-eglise")} data-testid={`${testPrefix}-handle`} />
          </div>
          <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>{tr("handle_hint")}</p>
        </div>
      )}
      {showMedia && (
        <div>
          <Label>{tr("owner")}</Label>
          <Input type="email" value={value.owner_email || ""} onChange={(e) => set("owner_email", e.target.value)} placeholder="responsable@email.com" data-testid={`${testPrefix}-owner-email`} />
          <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>{tr("owner_hint")}</p>
        </div>
      )}
    </div>
  );
};
